export class ApiError extends Error {
    status;
    code;
    detail;
    constructor(status, message, 
    /** Machine-readable error code from the server body (HttpError.code), when sent. */
    code, 
    /** The server's `detail` bag (HttpError.detail) — e.g. a validation 422's
     *  full `issues` list, which a form uses to badge the offending section
     *  rather than showing only the first message. */
    detail) {
        super(message);
        this.status = status;
        this.code = code;
        this.detail = detail;
        this.name = 'ApiError';
    }
}
// Identity owns these auth endpoints; each app's server only serves /api/auth/me.
// In dev the Vite proxy routes /api/auth/* (except /me) → identity, so a relative
// path works. A production build has no proxy, so these must go cross-origin to
// the identity host (with credentials — identity allows the app origins via CORS,
// and the rt_session cookie is parent-domain-scoped so it rides along).
const IDENTITY_AUTH_PATHS = new Set([
    '/api/auth/logout',
    '/api/auth/change-password',
    '/api/auth/switch-tenant',
    '/api/auth/profile',
    '/api/auth/access',
]);
function isIdentityAuthPath(path) {
    return IDENTITY_AUTH_PATHS.has(path) || path.startsWith('/api/auth/sso/');
}
export function createApi(opts) {
    function identityAuthUrl(path) {
        return opts.identityOrigin ? `${opts.identityOrigin}${path}` : path;
    }
    function resolveUrl(path) {
        if (isIdentityAuthPath(path)) {
            return identityAuthUrl(path);
        }
        return opts.basePath ? `${opts.basePath}${path}` : path;
    }
    async function request(path, init) {
        const res = await fetch(resolveUrl(path), {
            ...init,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...init?.headers },
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new ApiError(res.status, body.error ?? `HTTP ${res.status}`, body.code, body.detail);
        }
        return res.json();
    }
    const api = {
        get: (path) => request(path),
        post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) }),
        patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
        put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) }),
        delete: (path) => request(path, { method: 'DELETE' }),
    };
    // Enter a tenant (tenantId) or exit to org/platform view (null). Re-mints the
    // session cookie on id, then hard-navigates so the whole app picks up new
    // context. `landing` may be a path on this origin or an absolute URL on a
    // sibling host (the cookie is parent-domain-scoped, so it carries).
    async function switchTenant(tenantId, landing = '/') {
        await api.post('/api/auth/switch-tenant', { tenantId });
        window.location.href = landing;
    }
    // Best-effort server-side logout, then to the login screen carrying where we
    // are so re-login bounces straight back (identity validates the return host
    // against its allowlist).
    async function signOut() {
        const ret = encodeURIComponent(window.location.href);
        await api.post('/api/auth/logout', {}).catch(() => { });
        window.location.href = `${opts.idUrl}/login?return=${ret}`;
    }
    return { api, switchTenant, signOut, identityAuthUrl };
}
